// Helper function to split markdown into words, tracking the end index in the original string
export function splitMarkdownIntoDisplayWords(
  markdown: string
): { word: string; endIndex: number }[] {
  const result: { word: string; endIndex: number }[] = [];
  const formattingSymbols = ["**", "__", "*", "_", "~~", "`", "```"];

  // Helper to find the longest matching formatting symbol at a position
  function findFormattingAt(pos: number): string | null {
    return (
      formattingSymbols
        .filter((symbol) => markdown.slice(pos, pos + symbol.length) === symbol)
        .sort((a, b) => b.length - a.length)[0] || null
    );
  }

  // Helper to check if position is at start of line
  function isStartOfLine(pos: number): boolean {
    return pos === 0 || markdown[pos - 1] === "\n";
  }

  // Helper to check if position is at a list marker
  function isListMarkerAt(pos: number): boolean {
    if (isStartOfLine(pos)) {
      const ch = markdown[pos];
      if (
        (ch === "*" || ch === "-" || ch === "+") &&
        markdown[pos + 1] === " "
      ) {
        return true;
      }
    }
    return false;
  }

  // Helper to check if a character is part of a word
  function isWordChar(char: string): boolean {
    return /[\p{L}\p{N}]|[^\x00-\x7F]/u.test(char);
  }

  // Track formatting state
  type FormattingState = {
    symbol: string;
    startIndex: number;
    contentStartIndex: number;
  };
  const formattingStack: FormattingState[] = [];

  let i = 0;
  let currentWord = "";

  function pushCurrentWord(endIndex: number) {
    if (currentWord) {
      result.push({
        word: currentWord,
        endIndex: endIndex,
      });
      currentWord = "";
    }
  }

  while (i < markdown.length) {
    // Check for list markers first
    if (isListMarkerAt(i)) {
      pushCurrentWord(i);
      result.push({
        word: markdown[i] + " ",
        endIndex: i + 2,
      });
      i += 2; // Skip marker and space
      continue;
    }

    // Check for formatting symbols
    const symbol = findFormattingAt(i);
    if (symbol) {
      // Check if this is an opening or closing symbol
      const matchingFormat = formattingStack.find((f) => f.symbol === symbol);
      if (!matchingFormat) {
        // Opening symbol - push to stack
        pushCurrentWord(i);
        formattingStack.push({
          symbol,
          startIndex: i,
          contentStartIndex: i + symbol.length,
        });
        currentWord = symbol;
        i += symbol.length;
        continue;
      } else {
        // Closing symbol - include everything up to and including this symbol
        currentWord += symbol;
        result.push({
          word: currentWord,
          endIndex: i + symbol.length,
        });
        currentWord = "";
        formattingStack.splice(formattingStack.indexOf(matchingFormat), 1);
        i += symbol.length;
        continue;
      }
    }

    // Handle word boundaries
    if (!isWordChar(markdown[i])) {
      if (currentWord) {
        pushCurrentWord(i);
      }
      // Add non-word character as a separate token if it's not whitespace
      if (!/\s/.test(markdown[i])) {
        result.push({
          word: markdown[i],
          endIndex: i + 1,
        });
      }
      i++;
      continue;
    }

    // Regular character - add to current word
    currentWord += markdown[i];
    i++;
  }

  // Handle any remaining word
  pushCurrentWord(markdown.length);

  return result;
}

// Helper function to split content into words while preserving formatting
export function splitIntoWords(content: string): string[] {
  const words: string[] = [];
  let currentWord = "";
  let i = 0;

  while (i < content.length) {
    const char = content[i];

    if (char === "\n") {
      // Handle newlines - add current word if exists and add newline as separate token
      if (currentWord) {
        words.push(currentWord);
        currentWord = "";
      }
      words.push("\n");
    } else if (char === " ") {
      // Add space to the previous word if exists, otherwise start new word with space
      if (currentWord) {
        words.push(currentWord + char);
        currentWord = "";
      } else {
        currentWord = " ";
      }
    } else {
      // Regular character
      currentWord += char;
    }
    i++;
  }

  // Don't forget the last word
  if (currentWord) {
    words.push(currentWord);
  }

  return words;
}

// Helper function to close unclosed markdown formatting in a partial string
export function closeUnclosedMarkdownFormatting(partial: string): string {
  // Stack for open formatting
  const stack: string[] = [];
  // Track if we're inside a code block
  let insideCodeBlock = false;

  // Patterns for formatting
  const formattingPatterns = [
    { open: "```", close: "```", type: "codeblock" },
    { open: "`", close: "`", type: "inlinecode" },
    { open: "**", close: "**", type: "bold" },
    { open: "__", close: "__", type: "bold" },
    { open: "*", close: "*", type: "italic" },
    { open: "_", close: "_", type: "italic" },
    { open: "~~", close: "~~", type: "strike" },
  ];

  // Helper to check if a position is a list marker
  function isListMarkerAt(pos: number) {
    // List marker: start of string or after newline, followed by space
    if (pos === 0 || partial[pos - 1] === "\n") {
      const ch = partial[pos];
      if (
        (ch === "*" || ch === "-" || ch === "+") &&
        partial[pos + 1] === " "
      ) {
        return true;
      }
    }
    return false;
  }

  // Helper to check if a position is likely part of a URL
  function isLikelyUrl(pos: number): boolean {
    // Simple check: if we see http://, https://, or www. before the symbol
    const prevText = partial.slice(Math.max(0, pos - 10), pos);
    return /https?:\/\/|www\./.test(prevText);
  }

  let i = 0;
  while (i < partial.length) {
    let matched = false;
    // Skip list markers
    if (isListMarkerAt(i)) {
      i += 2; // skip marker and following space
      continue;
    }

    // Skip URLs
    if (isLikelyUrl(i)) {
      i++;
      continue;
    }

    for (const { open, type } of formattingPatterns) {
      if (partial.slice(i, i + open.length) === open) {
        // Special handling for code blocks
        if (type === "codeblock") {
          // Only match at start of line
          if (i !== 0 && partial[i - 1] !== "\n") {
            continue;
          }

          if (!insideCodeBlock) {
            // Opening code block
            insideCodeBlock = true;
            stack.push(open);
          } else {
            // Closing code block
            insideCodeBlock = false;
            stack.pop();
          }
          i += open.length;
          matched = true;
          break;
        }

        // Skip other formatting if we're inside a code block
        if (insideCodeBlock) {
          i++;
          continue;
        }

        // Don't treat single * or _ as formatting if:
        // 1. It's at the end of the text (likely incomplete)
        // 2. It's followed by a space or newline (likely a list marker)
        // 3. It's preceded by a space (likely a list marker)
        if ((open === "*" || open === "_") && open.length === 1) {
          const nextChar = partial[i + 1] || "";
          const prevChar = i > 0 ? partial[i - 1] : "";
          if (
            i === partial.length - 1 || // at end
            /[\s\n]/.test(nextChar) || // followed by space/newline
            /[\s\n]/.test(prevChar) || // preceded by space/newline
            !stack.includes(open) // no matching opening tag
          ) {
            i++;
            continue;
          }
        }

        // If top of stack is same, it's a close; else, it's an open
        if (stack.length > 0 && stack[stack.length - 1] === open) {
          stack.pop();
        } else {
          stack.push(open);
        }
        i += open.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      i++;
    }
  }

  // Only close formatting that was explicitly opened
  let closed = partial;
  while (stack.length > 0) {
    const open = stack.pop();
    // Don't close code blocks during partial rendering
    if (open === "```" && insideCodeBlock) {
      continue;
    }
    // Don't close single * or _ at the end
    if (open && (open.length > 1 || (open !== "*" && open !== "_"))) {
      const close =
        formattingPatterns.find((f) => f.open === open)?.close || "";
      closed += close;
    }
  }
  return closed;
}
